import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { Choice, type ChoiceProps } from '../../components';
import { useNewExamFormOptional } from '../../providers';
import { useExamPreviewFormOptional } from '../../providers/exam-preview';
import { type Choice as QuestionChoice } from '../../providers/new-exam/types';

interface ChoicesProps {
  name: string;
  choices?: QuestionChoice[];
}

function useExamFormContext() {
  const formContext = useFormContext();
  const newExamFormContext = useNewExamFormOptional();
  const previewFormContext = useExamPreviewFormOptional();

  const submitContext = newExamFormContext || previewFormContext;

  if (!submitContext) {
    throw new Error(
      'Choices must be used within either NewExamFormProvider or ExamPreviewFormProvider',
    );
  }

  return { ...formContext, ...submitContext };
}

function ChoiceContainer(props: ChoiceProps) {
  const { name, index } = props;
  const { watch, setValue, submitExam } = useExamFormContext();

  const q = name.split('.')[0];

  const answer = watch(`${q}.answer`);
  const eliminated = watch(`${q}.eliminated.${index}`);
  const currentlyAnswered = answer === String(index + 1);

  const handleChoiceSelect = useCallback(async () => {
    setValue(`${q}.answer`, String(index + 1));

    await submitExam();
  }, [q, index, setValue, submitExam]);

  useEffect(() => {
    if (eliminated && currentlyAnswered) {
      setValue(`${q}.answer`, undefined);
    }
  }, [eliminated]);

  return (
    <Choice {...props} showElimination={false} eliminated={false} onSelect={handleChoiceSelect} />
  );
}

export default function Choices({ name, choices }: ChoicesProps) {
  return (
    <>
      {choices?.map((choice, index) => {
        return <ChoiceContainer key={`${name}.${index}`} {...choice} index={index} name={name} />;
      })}
    </>
  );
}
